import { Button, TextInput, NumberInput, Select, ActionIcon, Tooltip, Stack, Text } from '@mantine/core';
import { IconPlus, IconTrash, IconX } from '@tabler/icons-react';
import { formatPrice } from '../../utils/formatPrice';
import { formatWeight } from '../../utils/formatWeight';
import { effectiveCustomerPrice } from '../../utils/adminCustomerPrice';
import { weightToPieces, weightFromPieces } from '../../utils/weightPieces';

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

const DEFAULT_VAT_RATES = { NORMAL: 8.1, REDUCED: 2.6, ACCOMMODATION: 3.8 };

function lineTotals(item) {
  const unitPrice = Number(item.unit_price) || 0;
  const quantity = Number(item.quantity) || 0;
  const discountPercent = Math.min(100, Math.max(0, Number(item.discount_percent) || 0));
  const gross = round2(unitPrice * quantity);
  const discount = round2(gross * (discountPercent / 100));
  return { gross, discount, net: round2(gross - discount) };
}

// Quantities are canonical pieces of packs; the chosen unit is just an alternative entry mode.
function isWeightEntryMode(item) {
  return item.unit !== 'pcs' && item.type !== 'adhoc' && Boolean(item.variant_id);
}

export default function OrderItemsTab({
  items,
  onChange,
  variantOptions,
  variantLookup,
  customerPricesByVariant,
  units,
  vatRates,
  cartDiscountPercent,
  onCartDiscountPercentChange,
  t,
}) {
  const updateItem = (index, patch) => {
    const next = items.map((it, i) => (i === index ? { ...it, ...patch } : it));
    onChange(next);
  };

  const syncAgreedPrice = (item, quantity, basePatch) => {
    // Keep the agreed price in sync when a volume-model customer price is in
    // effect and the price wasn't manually overridden.
    if (!item.priceAuto || item.type === 'adhoc' || !item.variant_id) return basePatch;
    const v = variantLookup.get(item.variant_id);
    const cp = customerPricesByVariant.get(item.variant_id);
    if (!v || !cp) return basePatch;
    const effective = effectiveCustomerPrice(v, cp, quantity);
    if (effective != null && !Number.isNaN(effective)) return { ...basePatch, unit_price: effective };
    return basePatch;
  };

  const handleVariantSelect = (index, variantId) => {
    const it = items[index];
    const v = variantId ? variantLookup.get(variantId) : null;
    if (!v) {
      updateItem(index, { variant_id: '', name: '', sku: '', unit_price: '', vat_rate: 8.1, unit: 'pcs', inputQty: undefined, priceAuto: false });
      return;
    }
    const cp = customerPricesByVariant.get(variantId);
    const effective = cp ? effectiveCustomerPrice(v, cp, it.quantity) : null;
    // Keep the admin's chosen entry mode; back-fill the weight display if convertible.
    const unit = it.unit || 'pcs';
    const inputQty = unit !== 'pcs' ? weightFromPieces(it.quantity, v.weight_value, v.weight_unit, unit) : undefined;
    updateItem(index, {
      variant_id: variantId,
      name: v.productName || v.sku || '',
      sku: v.sku || '',
      unit_price: effective != null && !Number.isNaN(effective) ? effective : Number(v.price),
      vat_rate: v.vat_rate != null ? Number(v.vat_rate) : 8.1,
      unit,
      inputQty: inputQty != null && inputQty > 0 ? Number(inputQty.toFixed(3)) : '',
      priceAuto: true,
    });
  };

  const handleQtyChange = (index, val) => {
    const it = items[index];
    if (isWeightEntryMode(it)) {
      const v = variantLookup.get(it.variant_id);
      if (!v) return;
      const num = Number(val);
      const pieces = (val === '' || val == null || !Number.isFinite(num) || num <= 0)
        ? 0
        : weightToPieces(num, it.unit, v.weight_value, v.weight_unit, { roundMode: 'up' }) ?? 0;
      updateItem(index, syncAgreedPrice(it, pieces, { inputQty: val ?? '', quantity: pieces }));
      return;
    }
    // A negative quantity is a refund (money back), so the sign is kept. Packs are
    // whole units, hence the truncation; the server validates the rest.
    const num = val == null ? Number.NaN : Number(val);
    const quantity = Number.isFinite(num) ? Math.trunc(num) : 1;
    updateItem(index, syncAgreedPrice(it, quantity, { quantity }));
  };

  // Switching units converts the displayed amount so the same physical order
  // survives; the canonical piece count never changes on a unit switch alone.
  const handleUnitChange = (index, nextUnit) => {
    const it = items[index];
    const unit = nextUnit || 'pcs';
    if (unit === it.unit) return;
    const v = it.variant_id ? variantLookup.get(it.variant_id) : null;
    const inputQty = unit === 'pcs' || !v
      ? undefined
      : weightFromPieces(Number(it.quantity) || 0, v.weight_value, v.weight_unit, unit);
    updateItem(index, { unit, inputQty: inputQty != null && inputQty > 0 ? Number(inputQty.toFixed(3)) : '' });
  };

  const addProductRow = () => {
    onChange([
      ...items,
      { type: 'product', variant_id: '', name: '', sku: '', quantity: 1, unit: 'pcs', inputQty: undefined, unit_price: '', vat_rate: 8.1, discount_percent: 0, priceAuto: false },
    ]);
  };

  const addAdhocRow = () => {
    onChange([
      ...items,
      { type: 'adhoc', variant_id: null, name: '', quantity: 1, unit: 'pcs', inputQty: undefined, unit_price: '', vat_rate: 8.1, discount_percent: 0, priceAuto: false },
    ]);
  };

  const removeItem = (index) => onChange(items.filter((_, i) => i !== index));

  const renderVariantSelect = (item, index) => (
    <Select
      label={t('takeOrderItemProduct', 'Product')}
      placeholder={t('takeOrderSearchProduct', 'Search product…')}
      data={variantOptions}
      value={item.variant_id || null}
      onChange={(val) => handleVariantSelect(index, val)}
      searchable
      clearable
      nothingFoundMessage={t('takeOrderNoProducts', 'No products found')}
      required
      aria-required="true"
    />
  );

  const renderQty = (item, index) => {
    const weightMode = isWeightEntryMode(item);
    const v = item.variant_id ? variantLookup.get(item.variant_id) : null;
    const isCredit = Number(item.quantity) < 0;
    const shipped = Number(item.total_shipped || 0);
    const locked = shipped > 0;
    const derived = isCredit
      ? t('takeOrderRefundQtyHint', 'Refund — money back, no stock or shipping')
      : weightMode && v && Number(item.quantity) > 0
        ? t('takeOrderPacksEquivalent', '= {n} × {pack}', {
            n: Math.ceil(Number(item.quantity)),
            pack: formatWeight(v.weight_value, v.weight_unit),
          })
        : locked ? t('takeOrderQtyLocked', 'Quantity locked (partially/fully shipped)') : '';
    return (
      <NumberInput
        label={t('takeOrderQty', 'Qty')}
        description={derived}
        value={weightMode ? (item.inputQty ?? '') : item.quantity}
        onChange={(val) => handleQtyChange(index, val)}
        // Weight is a physical amount, so it can never be negative; a piece count
        // can — that is how a refund is entered.
        min={weightMode ? 0.001 : -9999}
        step={weightMode ? 0.1 : 1}
        required
        aria-required="true"
        disabled={locked}
        readOnly={locked}
      />
    );
  };

  const renderUnit = (item, index) => {
    const shipped = Number(item.total_shipped || 0);
    const locked = shipped > 0;
    return (
      <Select
        label={t('takeOrderUnit', 'Unit')}
        data={units.map((u) => ({ value: u, label: u }))}
        value={item.unit || 'pcs'}
        onChange={(val) => handleUnitChange(index, val)}
        required
        aria-required="true"
        disabled={locked}
        readOnly={locked}
      />
    );
  };

  const renderPrice = (item, index) => {
    const v = item.variant_id ? variantLookup.get(item.variant_id) : null;
    const perPackHint = isWeightEntryMode(item) && v && v.weight_value != null
      ? t('takeOrderPricePerPack', 'per {pack} pack', { pack: formatWeight(v.weight_value, v.weight_unit) })
      : '';
    return (
      <div className="take-order-price-cell">
        <NumberInput
          label={t('takeOrderUnitPrice', 'Unit price (CHF)')}
          description={perPackHint}
          value={item.unit_price == null || item.unit_price === '' ? undefined : Number(item.unit_price)}
          onChange={(val) => updateItem(index, { unit_price: val == null ? '' : val, priceAuto: false })}
          decimalScale={2}
          fixedDecimalScale
          step={0.05}
          // A price is never negative; money back is expressed with a negative
          // QUANTITY, not a negative unit price.
          min={0}
          clampBehavior="strict"
          required
          aria-required="true"
        />
      </div>
    );
  };

  const renderDiscount = (item, index) => (
    <NumberInput
      label={t('takeOrderDiscountPct', 'Discount %')}
      value={Number(item.discount_percent) || 0}
      onChange={(val) => updateItem(index, { discount_percent: val == null ? 0 : val })}
      min={0}
      max={100}
      step={1}
      suffix=" %"
    />
  );

  const renderVat = (item, index) => {
    const rates = vatRates && typeof vatRates === 'object' ? vatRates : DEFAULT_VAT_RATES;
    const options = Object.entries(rates).map(([key, val]) => ({
      value: String(Number(val)),
      label: `${Number(val).toFixed(1)}% (${t(`takeOrderVatRate${key}`, key.charAt(0) + key.slice(1).toLowerCase())})`,
    }));
    return (
      <Select
        label={t('takeOrderVatRate', 'VAT')}
        data={options}
        value={item.vat_rate == null || item.vat_rate === '' ? undefined : String(Number(item.vat_rate))}
        onChange={(val) => updateItem(index, { vat_rate: val == null ? null : Number(val) })}
        required
        aria-required="true"
      />
    );
  };

  const renderRemove = (item, index) => {
    const locked = Number(item.total_shipped || 0) > 0;
    return (
      <Tooltip
        label={locked
          ? t('takeOrderRemoveShippedItem', 'Already fulfilled — cannot be removed')
          : t('takeOrderRemoveItem', 'Remove item')}
      >
        <ActionIcon
          variant="subtle"
          color="red"
          aria-label={t('takeOrderRemoveItem', 'Remove item')}
          onClick={() => removeItem(index)}
          // A fulfilled line (or one with an open delivery draft) must stay on the
          // running order: the goods have left the warehouse.
          disabled={locked}
        >
          <IconTrash size={16} />
        </ActionIcon>
      </Tooltip>
    );
  };

  return (
    <Stack gap="md">
      {items.length === 0 && (
        <Text size="sm" c="dimmed">
          {t('takeOrderNoItemsHint', 'No items yet. Add a product or a custom line below.')}
        </Text>
      )}

      {items.map((item, index) => {
        const totals = lineTotals(item);
        const isAdhoc = item.type === 'adhoc';
        const isCredit = Number(item.quantity) < 0;
        return (
          <div
            key={item.localId || index}
            className={`take-order-item-row${isAdhoc ? ' take-order-adhoc-row' : ''}${isCredit ? ' take-order-refund-row' : ''}`}
          >
            {isAdhoc ? (
              <TextInput
                label={t('takeOrderAdhocName', 'Custom line description')}
                placeholder={t('takeOrderAdhocPlaceholder', 'e.g. Rush delivery surcharge')}
                value={item.name || ''}
                onChange={(e) => updateItem(index, { name: e.currentTarget.value })}
                required
                aria-required="true"
              />
            ) : (
              renderVariantSelect(item, index)
            )}
            {renderQty(item, index)}
            {renderUnit(item, index)}
            {renderPrice(item, index)}
            {renderDiscount(item, index)}
            {isAdhoc ? renderVat(item, index) : <div />}
            <div className="take-order-item-end">
              <div className="take-order-item-subtotal" style={isCredit ? { color: '#c2410c', fontWeight: 600 } : undefined}>
                {formatPrice(totals.net)}
                <div className="take-order-item-line-total">
                  {isCredit
                    ? t('takeOrderRefundLine', 'Refund')
                    : totals.discount > 0 ? `${t('takeOrderLineDiscount', 'incl. discount')} −${Number(item.discount_percent) || 0}%` : ''}
                </div>
              </div>
              {renderRemove(item, index)}
            </div>
          </div>
        );
      })}

      <div className="take-order-add-bar">
        <Button
          variant="light"
          leftSection={<IconPlus size={16} />}
          onClick={addProductRow}
          aria-label={t('takeOrderAddProduct', 'Add product line')}
        >
          {t('takeOrderAddProduct', 'Add product')}
        </Button>
        <Button
          variant="light"
          color="orange"
          leftSection={<IconX size={16} />}
          onClick={addAdhocRow}
          aria-label={t('takeOrderAddAdhoc', 'Add custom line')}
        >
          {t('takeOrderAddAdhoc', 'Add custom line')}
        </Button>
      </div>
      {/* Order-wide discount. Resolved last — after the per-line discounts, the
          selected offers and the volume discount — so it applies to what is still
          owed. Leave empty for none. */}
      <div className="take-order-cart-discount">
        <NumberInput
          label={t('takeOrderCartDiscount', 'Discount on whole order (%)')}
          description={t('takeOrderCartDiscountHint', 'Applied after all other discounts. Lowers the total and the shipping tier it was matched to.')}
          value={cartDiscountPercent === '' || cartDiscountPercent == null ? '' : Number(cartDiscountPercent)}
          onChange={(val) => onCartDiscountPercentChange(val == null ? '' : val)}
          min={0}
          max={100}
          step={1}
          suffix=" %"
          allowDecimal
          w={220}
        />
      </div>
      <Text size="xs" c="dimmed">
        {t('takeOrderWeightHint', 'Weight-based products can be ordered by weight (e.g. kg) — the pack count is derived automatically and rounded up. A negative quantity is a refund: money back, with no stock or shipping.')}
      </Text>
      <Text size="xs" c="dimmed">
        {t('takeOrderItemsHint', 'Product lines draw price and VAT from the catalog and check stock. Custom lines are free-form text rows — they are never fulfilled or refunded.')}
      </Text>
    </Stack>
  );
}
